import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventIcon from '@mui/icons-material/Event';
import SettingsIcon from '@mui/icons-material/Settings';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import LockIcon from '@mui/icons-material/Lock';
import { Link, useLocation } from 'react-router-dom';
import { usePlayerData } from '../data/DataContext';
import { DS, Overline, numericSx } from '../shared/Dashboard';

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  isActive: (pathname: string) => boolean;
  locked?: boolean;
  badge?: number;
}

const itemSx = (active: boolean, locked: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  px: 1.5,
  py: 1,
  borderRadius: '10px',
  textDecoration: 'none',
  whiteSpace: 'nowrap' as const,
  flexShrink: 0,
  fontSize: '14px',
  fontWeight: active ? 700 : 600,
  color: locked ? DS.textFaint : active ? DS.brand : DS.textMuted,
  background: active ? alpha(DS.brand, 0.12) : 'transparent',
  border: `1px solid ${active ? alpha(DS.brand, 0.25) : 'transparent'}`,
  cursor: locked ? 'not-allowed' : 'pointer',
  transition: 'all 0.15s ease',
  '& svg': { fontSize: 18, flexShrink: 0 },
  ...(!locked && !active && {
    '&:hover': { color: DS.text, background: DS.inset },
  }),
});

const Sidebar = () => {
  const location = useLocation();
  const context = usePlayerData();
  const isAdmin = context?.isAdmin || false;
  const pendingCount = Object.keys(context?.pendingEvents || {}).length;
  const { pathname } = location;

  const items: NavItem[] = [
    {
      label: 'Dashboard',
      to: '/admin/dashboard',
      icon: <DashboardIcon />,
      isActive: (p) => p === '/admin/dashboard',
    },
    {
      label: 'Events',
      to: '/admin/events',
      icon: <FormatListBulletedIcon />,
      isActive: (p) => p === '/admin/events' || p.startsWith('/admin/edit-event'),
      badge: pendingCount,
    },
    {
      label: 'Add Event',
      to: '/admin/add-event',
      icon: <EventIcon />,
      isActive: (p) => p === '/admin/add-event',
    },
    {
      label: 'Queries',
      to: '/admin/database-queries',
      icon: <QueryStatsIcon />,
      isActive: (p) => p === '/admin/database-queries',
      locked: !isAdmin,
    },
    {
      label: 'Settings',
      to: '/admin/settings',
      icon: <SettingsIcon />,
      isActive: (p) => p === '/admin/settings',
    },
  ];

  return (
    <Box
      component="nav"
      sx={{
        position: { sm: 'sticky' },
        top: { sm: 16 },
        p: { xs: 1, sm: 1.5 },
        mb: { xs: 1, sm: 0 },
        background: DS.surface,
        border: `1px solid ${DS.border}`,
        borderRadius: DS.radius,
      }}
    >
      <Box sx={{ px: 1.5, pt: 0.5, pb: 1, display: { xs: 'none', sm: 'block' } }}>
        <Overline>Admin</Overline>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column' },
          gap: 0.5,
          overflowX: { xs: 'auto', sm: 'visible' },
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {items.map((item) => {
          const active = item.isActive(pathname);
          const locked = !!item.locked;

          const content = (
            <>
              {item.icon}
              <Typography component="span" sx={{ font: 'inherit', flex: 1 }}>
                {item.label}
              </Typography>
              {locked && <LockIcon sx={{ fontSize: '14px !important', color: DS.warning }} />}
              {!locked && !!item.badge && (
                <Box component="span" sx={{
                  minWidth: 20,
                  px: 0.75,
                  borderRadius: '999px',
                  background: alpha(DS.warning, 0.16),
                  color: DS.warning,
                  fontSize: '11px',
                  fontWeight: 700,
                  lineHeight: '18px',
                  textAlign: 'center',
                  ...numericSx,
                }}>
                  {item.badge}
                </Box>
              )}
            </>
          );

          if (locked) {
            return (
              <Tooltip key={item.to} title="Admins only" placement="right">
                <Box aria-disabled="true" sx={itemSx(active, true)}>
                  {content}
                </Box>
              </Tooltip>
            );
          }

          return (
            <Box
              key={item.to}
              component={Link}
              to={item.to}
              aria-current={active ? 'page' : undefined}
              sx={itemSx(active, false)}
            >
              {content}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default Sidebar;
